/* Servicio de la sección Recursos (biblioteca de capacitaciones y material).
 * Centraliza las llamadas al backend (prefijo /biblioteca/...).
 * El token de autenticación lo agrega el interceptor de ./api.
 */
import api from "./api";

// ----------------------------- Tipos -----------------------------

export type RecursoTipo = "VIDEO" | "DOCUMENTO" | "ENLACE";
export type RecursoProveedor = "CLOUDINARY" | "YOUTUBE" | "VIMEO" | "URL";
export type RecursoAudiencia = "GLOBAL" | "CARGOS";
export type RecursoEstado = "BORRADOR" | "PUBLICADO" | "ARCHIVADO";

export interface CategoriaRecurso {
  id: number;
  clave: string;
  nombre: string;
  descripcion: string | null;
  icono: string | null;
  orden: number;
  total: number;
}

export interface ProgresoRecurso {
  segundos_vistos: number;
  porcentaje: number;
  completado: boolean;
  ultima_posicion_seg: number;
}

export interface Recurso {
  id: number;
  categoria_id: number;
  categoria_clave: string;
  categoria_nombre: string;
  titulo: string;
  descripcion: string | null;
  tipo: RecursoTipo;
  proveedor: RecursoProveedor;
  public_id: string | null;
  url_hls: string | null;
  url_mp4: string | null;
  url_externa: string | null;
  url_poster: string | null;
  duracion_seg: number | null;
  bytes: number | null;
  audiencia: RecursoAudiencia;
  estado: RecursoEstado;
  vigente_desde: string | null;
  vigente_hasta: string | null;
  obligatorio: boolean | number;
  orden: number;
  creado_por: string | null;
  creado_por_nombre: string | null;
  fecha_creacion: string;
  actualizado_por?: string | null;
  fecha_actualizacion?: string | null;
  progreso?: ProgresoRecurso;
  /* Solo en la vista de administración */
  cargos?: string[];
  vistas?: number;
  completados?: number;
}

export interface RecursoPayload {
  categoria_id?: number;
  categoria_clave?: string;
  titulo: string;
  descripcion?: string | null;
  tipo: RecursoTipo;
  proveedor: RecursoProveedor;
  public_id?: string | null;
  url_mp4?: string | null;
  url_externa?: string | null;
  url_poster?: string | null;
  duracion_seg?: number | null;
  bytes?: number | null;
  audiencia: RecursoAudiencia;
  cargos?: string[];
  estado: RecursoEstado;
  vigente_desde?: string | null;
  vigente_hasta?: string | null;
  obligatorio?: boolean;
  orden?: number;
}

export interface CargoOrganizacion {
  cargo: string;
  es_adm: boolean;
}

export interface VistaRecurso {
  cedula: string;
  segundos_vistos: number;
  porcentaje_max: number;
  completado: boolean | number;
  fecha_inicio: string;
  fecha_ultima: string;
  fecha_completado: string | null;
}

/** Datos que el backend firma para subir el video directo al CDN. */
export interface FirmaSubida {
  upload_url: string;
  api_key: string;
  cloud_name: string;
  timestamp: number;
  signature: string;
  folder: string;
  eager: string;
  eager_async: string;
}

export interface VideoSubido {
  public_id: string;
  duracion_seg: number | null;
  bytes: number | null;
}

// ----------------------------- Helpers -----------------------------

/** Mensaje de error legible a partir de la respuesta del backend. */
const mensajeError = (error: any, porDefecto: string): string =>
  error?.response?.data?.error || error?.message || porDefecto;

// ----------------------------- Funcionario -----------------------------

export const listarCategorias = async (): Promise<CategoriaRecurso[]> => {
  try {
    const { data } = await api.get("biblioteca/categorias");
    return data?.datos ?? [];
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudieron obtener las categorías."));
  }
};

export const listarRecursos = async (categoria?: string): Promise<Recurso[]> => {
  try {
    const { data } = await api.get("biblioteca/listado", {
      params: categoria ? { categoria } : undefined,
    });
    return data?.datos ?? [];
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudieron obtener los recursos."));
  }
};

export const obtenerRecurso = async (id: number): Promise<Recurso> => {
  try {
    const { data } = await api.get(`biblioteca/${id}`);
    return data?.datos;
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo obtener el recurso."));
  }
};

/**
 * Guarda el avance de reproducción.
 *
 * Se llama con baja frecuencia (cada ~15 s y al pausar/terminar), no en cada
 * `timeupdate`: el evento dispara ~4 veces por segundo y eso serían cientos de
 * POST por video. Los errores se tragan a propósito — perder un tick de
 * progreso no debe cortarle la reproducción al usuario.
 */
export const guardarProgreso = async (
  id: number,
  segundosVistos: number,
  posicionSeg: number
): Promise<ProgresoRecurso | null> => {
  try {
    const { data } = await api.post(`biblioteca/${id}/progreso`, {
      segundos_vistos: Math.round(segundosVistos),
      posicion_seg: Math.round(posicionSeg),
    });
    return data?.datos ?? null;
  } catch {
    return null;
  }
};

// ----------------------------- Administración -----------------------------

export const esAdminRecursos = async (): Promise<boolean> => {
  try {
    const { data } = await api.get("biblioteca/admin/es-admin");
    return Boolean(data?.es_admin);
  } catch {
    return false;
  }
};

export const adminListarRecursos = async (categoria?: string): Promise<Recurso[]> => {
  try {
    const { data } = await api.get("biblioteca/admin/recursos", {
      params: categoria ? { categoria } : undefined,
    });
    return data?.datos ?? [];
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudieron obtener los recursos."));
  }
};

export const adminCrearRecurso = async (payload: RecursoPayload): Promise<number> => {
  try {
    const { data } = await api.post("biblioteca/admin/recursos", payload);
    return data?.id;
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo crear el recurso."));
  }
};

export const adminActualizarRecurso = async (
  id: number,
  payload: RecursoPayload
): Promise<void> => {
  try {
    await api.put(`biblioteca/admin/recursos/${id}`, payload);
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo actualizar el recurso."));
  }
};

export const adminCambiarEstado = async (id: number, estado: RecursoEstado): Promise<void> => {
  try {
    await api.post(`biblioteca/admin/recursos/${id}/estado`, { estado });
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo cambiar el estado."));
  }
};

export const adminEliminarRecurso = async (id: number): Promise<void> => {
  try {
    await api.delete(`biblioteca/admin/recursos/${id}`);
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo eliminar el recurso."));
  }
};

export const adminListarCargos = async (): Promise<CargoOrganizacion[]> => {
  try {
    const { data } = await api.get("biblioteca/admin/cargos");
    return data?.datos ?? [];
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudieron obtener los cargos."));
  }
};

export const adminVistasRecurso = async (id: number): Promise<VistaRecurso[]> => {
  try {
    const { data } = await api.get(`biblioteca/admin/recursos/${id}/vistas`);
    return data?.datos ?? [];
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudieron obtener las visualizaciones."));
  }
};

/**
 * Sube un video al CDN SIN pasar por el API.
 *
 * El backend solo emite una firma; el archivo viaja del navegador a Cloudinary.
 * Va con XMLHttpRequest en vez de axios porque necesitamos `upload.onprogress`
 * para mostrar una barra real: un video de capacitación puede tardar minutos
 * desde un celular y sin feedback el admin cree que se colgó.
 */
export const subirVideo = async (
  archivo: File,
  onProgreso?: (porcentaje: number) => void
): Promise<VideoSubido> => {
  let firma: FirmaSubida;
  try {
    const { data } = await api.post("biblioteca/admin/firma-subida");
    firma = data?.datos;
    if (!firma?.upload_url) throw new Error("Firma incompleta");
  } catch (error) {
    throw new Error(mensajeError(error, "No se pudo preparar la subida."));
  }

  /* Solo el tramo del XHR va envuelto en una Promise: un executor `async`
     se tragaría cualquier excepción previa y dejaría la promesa sin resolver,
     con la UI colgada en "subiendo". */
  return new Promise<VideoSubido>((resolve, reject) => {
    const form = new FormData();
    form.append("file", archivo);
    form.append("api_key", firma.api_key);
    form.append("timestamp", String(firma.timestamp));
    form.append("signature", firma.signature);
    form.append("folder", firma.folder);
    form.append("eager", firma.eager);
    form.append("eager_async", firma.eager_async);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", firma.upload_url, true);

    xhr.upload.onprogress = (evento) => {
      if (evento.lengthComputable && onProgreso) {
        onProgreso(Math.round((evento.loaded / evento.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        let detalle = "";
        try {
          detalle = JSON.parse(xhr.responseText)?.error?.message || "";
        } catch {
          /* respuesta no-JSON del CDN: nos quedamos con el status */
        }
        reject(new Error(detalle || `El CDN rechazó la subida (${xhr.status}).`));
        return;
      }
      try {
        const respuesta = JSON.parse(xhr.responseText);
        resolve({
          public_id: respuesta.public_id,
          duracion_seg: respuesta.duration ? Math.round(respuesta.duration) : null,
          bytes: respuesta.bytes ?? null,
        });
      } catch {
        reject(new Error("Respuesta inválida del CDN."));
      }
    };

    xhr.onerror = () => reject(new Error("Falló la conexión con el CDN."));
    xhr.onabort = () => reject(new Error("Subida cancelada."));

    xhr.send(form);
  });
};

// ----------------------------- Utilidades de presentación -----------------------------

/** Formatea segundos como "7:05" o "1:02:30". */
export const formatearDuracion = (segundos?: number | null): string => {
  if (!segundos || segundos <= 0) return "";
  const h = Math.floor(segundos / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  const s = Math.floor(segundos % 60);
  const dosDigitos = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${dosDigitos(m)}:${dosDigitos(s)}` : `${m}:${dosDigitos(s)}`;
};

/** URL de embed para YouTube/Vimeo a partir del recurso guardado. */
export const urlEmbed = (recurso: Recurso): string | null => {
  if (recurso.proveedor === "YOUTUBE" && recurso.public_id) {
    // nocookie + sin videos relacionados de otros canales.
    return `https://www.youtube-nocookie.com/embed/${recurso.public_id}?rel=0&modestbranding=1&playsinline=1`;
  }
  if (recurso.proveedor === "VIMEO" && recurso.public_id) {
    return `https://player.vimeo.com/video/${recurso.public_id}`;
  }
  return null;
};
